<template>
	<div class="drag-and-drop-file">
		<div @dragover="dragover" @dragleave="dragleave" @drop="drop">
			<div class="drag-and-drop-file__drop-wrapper--input-wrapper">
				<label for="assetsFieldHandle">
					<div v-show="files == null || files.length === 0" class="drag-and-drop-file__drop-text">{{ $t('common.addFile') }}: .png, .jpg, .jpeg</div>
					<div v-if="!isMultiple" class="drag-and-drop-file__remove-wrapper">
						<div v-if="files != null && files.length > 0">
							{{ files[0].name }}
						</div>
						<div v-if="files != null && files.length > 0" @click="removeFiles()">
							<MIcon icon="close" width="12" height="12" />
						</div>
					</div>
					<div v-if="isMultiple" class="drag-and-drop-file__remove-wrapper-multiple">
						<div v-for="(file, index) in files" :key="index" class="drag-and-drop-file__remove-wrapper drag-and-drop-file__remove-wrapper--multi">
							<div v-if="file != null">
								{{ file.name }}
							</div>
							<div v-if="file != null" @click="removeFiles(index)">
								<KIcon icon="close" width="12" height="12" />
							</div>
						</div>
					</div>
				</label>
				<KButton
					id="upload-file"
					normal
					:label="$t('common.selectFile')"
					@click="triggerFileInput"
				/>
				<input
					class="drag-and-drop-file__none"
					type="file"
					name="orderPhoto"
					:id="inputId"
					@change="onChange"
					ref="fileInput"
					:accept="acceptance"
					:multiple="isMultiple"
				/>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { defineProps, ref } from 'vue';
import { toast } from 'vue3-toastify';
import KIcon from '@/components/atoms/KIcon.vue';
import KButton from '@/components/atoms/KButton.vue';

interface IOwnProps {
	inputId: string;
	acceptance: string;
	onFilesChange: (files: File[]) => void;
	isMultiple?: boolean;
}
interface IProps extends IOwnProps {
}

const props = defineProps<IProps>();

const files = ref<File[]>([]);
const onChange = (event:any) => {
	if (!props.isMultiple) {
		files.value = event.target.files;
	} else {
		if (files.value.length > 0) {
			const newFiles = Array.from(event.target.files);
			files.value = [...files.value, ...newFiles] as File[];
		} else {
			files.value = Array.from(event.target.files);
		}
	}
	props.onFilesChange(files.value);
};
const dragover = (event:any) => {
	event.preventDefault();
	if (!event.currentTarget.classList.contains('drag-and-drop-file__bg-green-300')) {
		event.currentTarget.classList.remove('drag-and-drop-file__bg-gray-100');
		event.currentTarget.classList.add('drag-and-drop-file__bg-green-300');
	}
};
const dragleave = (event:any) => {
	event.currentTarget.classList.add('drag-and-drop-file__bg-gray-100');
	event.currentTarget.classList.remove('drag-and-drop-file__bg-green-300');
};
const drop = (event:any) => {
	event.preventDefault();
	const rules = props.acceptance.split(',');
	let isWrongFile:boolean = false;

	event.dataTransfer.files.forEach((file: File) => {
		let extension = file.name.split('.')[1];
		extension = '.' + extension;
		const findRule = rules.find((rule) => rule === extension);
		if (null == findRule) {
			toast.error(`${t('common.toast.wrongFile')}`);
			isWrongFile = true;
		}
	});
	if (isWrongFile) return;
	if (!props.isMultiple) {
		files.value = event.dataTransfer.files;
	} else {
		if (files.value.length > 0) {
			const newFiles = Array.from(event.dataTransfer.files);
			files.value = [...files.value, ...newFiles] as File[];
		} else {
			files.value = Array.from(event.dataTransfer.files);
		}
	}
	props.onFilesChange(files.value);
	event.currentTarget.classList.add('drag-and-drop-file__bg-gray-100');
	event.currentTarget.classList.remove('drag-and-drop-file__bg-green-300');
};
const removeFiles = (index?: number) => {
	if (!props.isMultiple) {
		files.value = [];
	} else {
		files.value.splice(null != index ? index : 0, 1);
	}
	props.onFilesChange(files.value);
};
const fileInput = ref<any>(null);
const triggerFileInput = () => {
	fileInput.value.click();
};
</script>

<style scoped lang="scss">
.drag-and-drop-file {
	border: 2px dashed $grey-400;
	border-radius: 8px;
	margin-bottom: 10px;

	&__drop-wrapper {
		border: 2px dashed $grey-400;
		border-radius: 8px;
		margin-bottom: 10px;

		&--error {
			border-color: $red-400;
		}

		&--input-wrapper {
			padding: 15px;
			height: 158px;
			display: flex;
			flex-direction: column;
			align-items: center;
			justify-content: center;
			position: relative;

			input[type='file'] {
				position: absolute;
				z-index: 1;
				top: 70%;
				left: 50%;
				transform: translate(-50%, -50%);
				width: 158px;
				height: 40px;
				opacity: 0;
				cursor: pointer;
			}

			label {
				margin-bottom: 20px;
			}
		}
	}

	&__file-btn {
		background-color: $primary-400;
		cursor: pointer;
		border-radius: 8px;
		color: $white;
		font-size: 14px;
		font-weight: 600;
		padding-top: 9px;
		text-align: center;
		position: absolute;
		top: 70%;
		left: 50%;
		transform: translate(-50%, -50%);
		width: 158px;
		height: 40px;
		transition: opacity 0.3s;

		&:hover {
			opacity: 0.5;
		}
	}

	&__remove-wrapper {
		display: flex;
		align-items: center;
		justify-content: center;

		div:nth-child(2) {
			margin-left: 10px;
			margin-top: 3px;
			cursor: pointer;
			transition: opacity 0.3s;

			&:hover {
				opacity: 0.5;
			}
		}

		&--multi {
			justify-content: flex-start;
		}
	}

	&__remove-wrapper-multiple{
		display: flex;
		align-items: center;
		justify-content: center;
		flex-wrap: wrap;
		gap: 10px;
	}

	&__drop-text {
		font-size: 12px;
		color: $grey-400;
		text-align: center;
	}

	&__bg-green-300 {
		background-color: $green-400;
		padding: 20px;
		border-radius: 8px;

		@include media-breakpoint-up(md) {
			padding: 30px;
		}
	}

	&__bg-gray-100 {
		background-color: $grey-400;
		padding: 20px;
		border-radius: 8px;

		@include media-breakpoint-up(md) {
			padding: 30px;
		}
	}

	&__none {
		display: none;
	}
}
</style>
